import React, { useRef, useEffect, useState } from "react"
import { Box, Flex } from "@rebass/grid/emotion"
import { debounce } from "lodash"
import useIsMobileResize from "../../../../hooks/useIsMobileResize"

import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"
import {
  ContainerWithBg,
  HeroDivider,
  HeroDividerHorizontal,
  HeroContainerDesktop,
  HeroDesktopLeft,
  HeroDesktopRight,
  HeroDesktopRightFlex,
  TabTransition,
} from "./hero.styled"
import Tabs from "../tabs/tabs"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"

const DEVMODE = process.env.GATSBY_DEVMODE === "1"
const LOG = (txt) => {
  if (
    typeof window !== `undefined` &&
    window.location.host !== `nutrafol.com`
  ) {
    console.info(txt)
  }
}

const HeroDesktop = ({
  data,
  setCoreFade,
  newCore,
  TitleSection,
  BenefitsSection,
  ImagesSectionFn,
}) => {
  const { subheading, pill, tabs } = data
  const imgRef = useRef(null)
  const dividerRef = useRef(null)
  const dividerHorRef = useRef(null)
  const benefitsRef = useRef(null)
  const [finished, setFinished] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [verticalFinished, setVerticalFinished] = useState(false)
  const isMobile = useIsMobileResize(1279)
  const triggerPoint = 150
  const timeline = gsap.timeline()
  const scrollTrigger = {
    invalidateOnRefresh: true,
    trigger: `.badge-container`,
    markers: false,
    start: `top-=200 top`,
    end: `top-=${triggerPoint} top`,
    scrub: 0.3,
  }

  const imagesGrow = (duration = 0.8) => {
    return gsap.to(imgRef.current, {
      transform: "scale(1)",
      duration,
      paused: true,
    })
  }
  const divHorizontal2button = (duration = 1) => {
    const benefitsW = benefitsRef.current.offsetWidth
    return gsap.to(dividerHorRef.current, {
      width: `calc(50% + ${benefitsW + 4}px)`,
      duration,
      paused: true,
      onComplete: () => {
        setFinished(true)
        gsap.killTweensOf(dividerHorRef.current)
      },
    })
  }

  const triggerTimeline = (text, position = 0, duration = 1) => {
    if (finished || playing) {
      return
    }
    LOG(text)
    LOG("---")
    setPlaying(true)
    const ease = "none"
    //const ease = "circ.in"

    timeline.to(
      dividerRef.current,
      {
        onComplete: () => {
          setVerticalFinished(true)
          gsap.killTweensOf(dividerRef.current)
          //images grow
          imagesGrow().play()
          //horizontal movements
          divHorizontal2button(duration).play()
        },
        duration,
        ease,
        height: "50%",
      },
      position
    )
  }

  useEffect(() => {
    if (finished) {
      gsap.killTweensOf(dividerHorRef.current)
      gsap.killTweensOf(dividerRef.current)
    }
  }, [finished])

  /**
   * handle large screen and reload issue
   */
  useEffect(() => {
    if (isMobile) {
      return
    }

    setTimeout(() => {
      if (finished || playing) {
        if (finished) {
          //animation was played. then  resized to and from mobile. lets redraw the horizontal animation
          imagesGrow(0.2).play()
          divHorizontal2button(0.2).play()
        }
        return
      }
      const rect = document
        .querySelector(`.badge-container`)
        ?.getBoundingClientRect()
      if (rect?.top < triggerPoint) {
        triggerTimeline("autoplay on badge pos")
      } else if (window.innerHeight > 1499) {
        triggerTimeline("autoplay on screen size", 2)
      }
    }, 150)
  }, [isMobile])

  //last resort to handle edge cases
  useEffect(() => {
    if (isMobile || finished || playing) {
      return
    }
    const handleScroll = () => {
      LOG("window.scrollY " + window.scrollY)
      if (window.scrollY > 55) {
        const dividerRefH = parseInt(dividerRef.current?.style?.height)
        // kick animation if dividerRef stuck  on 15%
        if (dividerRefH <= 15) {
          triggerTimeline(`autoplay below 55px`)
        }
        window.removeEventListener("scroll", debouncedHandleScroll)
      }
    }

    const debouncedHandleScroll = debounce(handleScroll, 500)

    window.addEventListener("scroll", debouncedHandleScroll)

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll)
    }
  }, [isMobile, finished, playing])

  useGSAP(() => {
    const mm = gsap.matchMedia()

    /*
     * see https://gsap.com/docs/v3/Eases/
     * trigger animation without scroll  on large screens
     */

    mm.add("(min-width: 1280px)", () => {
      if (finished) return
      //const ease = "power1.out"
      const ease = "none"
      gsap.to(dividerRef.current, {
        onComplete: () => {
          gsap.killTweensOf(dividerRef.current)
          triggerTimeline("autoplay onComplete scroll")
        },
        height: "15%",
        ease,
        scrollTrigger,
      })
    })
  }, [])

  /*
   * NT2-20132  ST2 [WILD_QA] HP Redesign | Hero Animation | PART 2
   * section height 734 + 900 = 1634px for large screens
   */
  const height = "clamp(360px, 55vh, 547px)"

  return (
    <>
      <HeroContainerDesktop className={`hero-trigger`}>
        <HeroDesktopLeft width={[1 / 2]}>
          <Flex
            alignItems={["center"]}
            width={[1]}
            height={[height]}
            mt={[0]}
            className={`top-flex`}
          >
            <Box width={[360]} mx="auto">
              <img src={pill} alt="" role="presentation" />
            </Box>
          </Flex>
          <Flex
            height={[1087]}
            alignItems={["center"]}
            width={[1]}
            className={`relative left-trigger`}
          >
            <HeroDivider
              ref={dividerRef}
              className={
                verticalFinished ? "vertical-finished" : "vertical-playing"
              }
            />
            <ContainerWithBg width={[1]}>
              <HeroDividerHorizontal
                ref={dividerHorRef}
                className={
                  verticalFinished ? "vertical-finished" : "vertical-playing"
                }
              />
              {ImagesSectionFn(imgRef)}
            </ContainerWithBg>
          </Flex>
        </HeroDesktopLeft>
        <HeroDesktopRight width={[1 / 2]}>
          <Flex
            alignItems={["center"]}
            width={[1]}
            height={[height]}
            className={`top-flex top-flex-right`}
          >
            <Box width={[1]}>{TitleSection}</Box>
          </Flex>
          <HeroDesktopRightFlex height={[1087]}>
            <Box className={`benefits`} ref={benefitsRef} pt={[160]}>
              <Box maxWidth={[527]} width={["auto"]} flexGrow={[1]}>
                <Box maxWidth={[524]} pt={[0]} pb={[80]}>
                  <TextElementNova
                    text={subheading}
                    element="h2"
                    className="Title-20-Super-Normal"
                  />
                </Box>
                {BenefitsSection}
              </Box>
            </Box>
            <TabTransition
              minWidth={[171]}
              alignSelf={`center`}
              className={`tab-${newCore}`}
            >
              <Tabs tabs={tabs} setter={setCoreFade} active={newCore} />
            </TabTransition>
          </HeroDesktopRightFlex>
        </HeroDesktopRight>
      </HeroContainerDesktop>
    </>
  )
}

export default HeroDesktop
